import { Dispatch } from "react";
import { getCollectionsApi, addCollectionApi, updateCollectionApi, deleteCollectionApi } from "../../api/collectionApi";
import { CollectionResponse } from "../../models/responses/CollectionResponse";

export const LOAD_COLLECTIONS_REQUEST = 'LOAD_COLLECTIONS_REQUEST';
export const LOAD_COLLECTIONS_SUCCESS = 'LOAD_COLLECTIONS_SUCCESS';
export const LOAD_COLLECTIONS_ERROR = 'LOAD_COLLECTIONS_ERROR';
export const SET_SELECTED_COLLECTION = 'SET_SELECTED_COLLECTION';
export const CLEAR_SELECTED_COLLECTION = 'CLEAR_SELECTED_COLLECTION';
export const SET_SYSTEM_COLLECTION_ID = 'SET_SYSTEM_COLLECTION_ID';
export const ADD_COLLECTION_SUCCESS = 'ADD_COLLECTION_SUCCESS';
export const ADD_COLLECTION_ERROR = 'ADD_COLLECTION_ERROR';
export const UPDATE_COLLECTION_SUCCESS = 'UPDATE_COLLECTION_SUCCESS';
export const UPDATE_COLLECTION_ERROR = 'UPDATE_COLLECTION_ERROR';
export const DELETE_COLLECTION_SUCCESS = 'DELETE_COLLECTION_SUCCESS';
export const DELETE_COLLECTION_ERROR = 'DELETE_COLLECTION_ERROR';
export const COLLECTION_OPERATION_LOADING = 'COLLECTION_OPERATION_LOADING';

export function loadCollectionsRequest() {
    return { type: LOAD_COLLECTIONS_REQUEST };
}

export function loadCollectionsSuccess(payload: CollectionResponse[]) {
    return { type: LOAD_COLLECTIONS_SUCCESS, payload: payload };
}

export function loadCollectionsError(error: any) {
    return { type: LOAD_COLLECTIONS_ERROR, payload: error };
}

export function updateCollectionError(error: any) {
    return { type: UPDATE_COLLECTION_ERROR, payload: error };
}

export function deleteCollectionError(error: any) {
    return { type: DELETE_COLLECTION_ERROR, payload: error };
}

export function collectionOperationLoading(isLoading: boolean) {
    return { type: COLLECTION_OPERATION_LOADING, payload: isLoading };
}

export function setSystemCollectionId(payload: number) {
    return { type: SET_SYSTEM_COLLECTION_ID, payload: payload };
}

function getSystemCollectionId(collections: CollectionResponse[]) {
    return collections.find(c => c.IsSystemCollection)?.Id || null;
}

export const loadCollections = () => {
    return async (dispatch: Dispatch<any>) => {
        dispatch(loadCollectionsRequest());

        try {
            const response = await getCollectionsApi();

            if (response?.data) {
                const collections: CollectionResponse[] = response.data.map((item: any) => ({
                    Id: item.id,
                    Name: item.name,
                    DateCreatedUtc: item.dateCreatedUtc,
                    VectorStoreCollectionName: item.vectorStoreCollectionName,
                    DateUpdatedUtc: item.dateUpdatedUtc,
                    IsSystemCollection: item.isSystemCollection,
                    IsSourceCollection: item.isSourceCollection,
                    ParentCollectionId: item.parentCollectionId ?? null
                }));

                dispatch(loadCollectionsSuccess(collections));
                const systemCollectionId = getSystemCollectionId(collections);
                if (systemCollectionId) {
                    dispatch(setSystemCollectionId(systemCollectionId));
                }

                return collections;
            } else {
                dispatch(loadCollectionsError(response?.message || 'Failed to load collections'));
                return null;
            }
        } catch (error) {
            dispatch(loadCollectionsError(error));
            return null;
        }
    };
};

export const initializeCollections = () => {
    return async (dispatch: Dispatch<any>) => {
        try {
            dispatch(loadCollectionsRequest());
            const response = await getCollectionsApi();

            if (response?.data) {
                const collections: CollectionResponse[] = response.data.map((item: any) => ({
                    Id: item.id,
                    Name: item.name,
                    DateCreatedUtc: item.dateCreatedUtc,
                    VectorStoreCollectionName: item.vectorStoreCollectionName,
                    DateUpdatedUtc: item.dateUpdatedUtc,
                    IsSystemCollection: item.isSystemCollection,
                    IsSourceCollection: item.isSourceCollection,
                    ParentCollectionId: item.parentCollectionId ?? null
                }));

                dispatch(loadCollectionsSuccess(collections));
                const systemCollectionId = getSystemCollectionId(collections);
                if (systemCollectionId) {
                    dispatch(setSystemCollectionId(systemCollectionId));
                }
            } else {
                dispatch(loadCollectionsError(response?.message || 'Failed to load collections'));
            }
        } catch (error) {
            dispatch(loadCollectionsError(error));
        }
    };
};

export const addCollection = (collectionData: { name: string; parentCollectionId?: number | null }) => {
    return async (dispatch: Dispatch<any>) => {
        dispatch(collectionOperationLoading(true));

        try {
            const collectionModel: Partial<CollectionResponse> = {
                Name: collectionData.name,
                ParentCollectionId: collectionData.parentCollectionId ?? null,
            };

            const response = await addCollectionApi(collectionModel as CollectionResponse);

            if (response?.data) {
                const freshResponse = await getCollectionsApi();
                if (freshResponse?.data) {
                    const freshCollections: CollectionResponse[] = freshResponse.data.map((item: any) => ({
                        Id: item.id,
                        Name: item.name,
                        DateCreatedUtc: item.dateCreatedUtc,
                        VectorStoreCollectionName: item.vectorStoreCollectionName,
                        DateUpdatedUtc: item.dateUpdatedUtc,
                        IsSystemCollection: item.isSystemCollection,
                        IsSourceCollection: item.isSourceCollection,
                        ParentCollectionId: item.parentCollectionId ?? null,
                    }));

                    dispatch(loadCollectionsSuccess(freshCollections));
                }

                dispatch(collectionOperationLoading(false));
                return { success: true, data: response.data };
            } else {
                dispatch(collectionOperationLoading(false));
                return { success: false, error: response?.message };
            }
        } catch (error: any) {
            dispatch(collectionOperationLoading(false));
            const message = error?.response?.data?.errorMessage || error?.message || 'Unknown error';
            return { success: false, error: message };
        }
    };
};

export const updateCollection = (collectionModel: CollectionResponse) => {
    return async (dispatch: Dispatch<any>) => {
        dispatch(collectionOperationLoading(true));

        try {
            const response = await updateCollectionApi(collectionModel);

            if (response?.data) {
                const freshResponse = await getCollectionsApi();
                if (freshResponse?.data) {
                    const collections: CollectionResponse[] = freshResponse.data.map((item: any) => ({
                        Id: item.id,
                        Name: item.name,
                        DateCreatedUtc: item.dateCreatedUtc,
                        VectorStoreCollectionName: item.vectorStoreCollectionName,
                        DateUpdatedUtc: item.dateUpdatedUtc,
                        IsSystemCollection: item.isSystemCollection,
                        IsSourceCollection: item.isSourceCollection,
                        ParentCollectionId: item.parentCollectionId ?? null,
                    }));

                    dispatch(loadCollectionsSuccess(collections));
                }

                dispatch(collectionOperationLoading(false));
                return { success: true, data: response.data };
            } else {
                dispatch(updateCollectionError(response?.message || 'Failed to update collection'));
                dispatch(collectionOperationLoading(false));
                return { success: false, error: response?.message };
            }
        } catch (error: any) {
            const message = error?.response?.data?.errorMessage || error?.message || 'Unknown error';
            dispatch(updateCollectionError(message));
            dispatch(collectionOperationLoading(false));
            return { success: false, error: message };
        }
    };
};

// Delete collection
export const deleteCollection = (collectionId: number) => {
    return async (dispatch: Dispatch<any>) => {
        dispatch(collectionOperationLoading(true));

        try {
            const response = await deleteCollectionApi(collectionId);

            if (response?.success || response?.data) {
                const freshResponse = await getCollectionsApi();
                if (freshResponse?.data) {
                    const collections: CollectionResponse[] = freshResponse.data.map((item: any) => ({
                        Id: item.id,
                        Name: item.name,
                        DateCreatedUtc: item.dateCreatedUtc,
                        VectorStoreCollectionName: item.vectorStoreCollectionName,
                        DateUpdatedUtc: item.dateUpdatedUtc,
                        IsSystemCollection: item.isSystemCollection,
                        IsSourceCollection: item.isSourceCollection,
                        ParentCollectionId: item.parentCollectionId ?? null,
                    }));

                    dispatch(loadCollectionsSuccess(collections));
                }

                dispatch(collectionOperationLoading(false));

                return { success: true };
            } else {
                const msg = response?.message || response?.errorMessage || 'Failed to delete collection';
                dispatch(deleteCollectionError(msg));
                dispatch(collectionOperationLoading(false));
                return { success: false, error: msg };
            }
        } catch (error: any) {
            const message = error?.response?.data?.errorMessage || error?.message || 'Unknown error';
            dispatch(deleteCollectionError(message));
            dispatch(collectionOperationLoading(false));
            return { success: false, error: message };
        }
    };
};